import React, { useCallback } from 'react'
import { HeadlessTableContext } from './headless-table-context'
import { composeEventHandlers } from '../utils/compose-event-handlers'
import { OverrideComponentProps } from '../utils/overridable-component-helper'
import { getParamName } from './get-table-state'
import { composeRefs } from '../utils/compose-refs'
import { useHydrated } from '../utils/use-hydrated'

type ParamName = 'sort' | 'search' | 'limit' | 'skip' | 'quickFilter' | `filters.${string}`

function useChangeParam() {
  const context = React.useContext(HeadlessTableContext)

  const changeParam = React.useCallback(
    (params: URLSearchParams, rawParamName: ParamName, paramValue: any) => {
      const isFilter = rawParamName.startsWith('filters.')
      const paramName = isFilter
        ? getParamName(context.prefix, rawParamName.replace('filters.', ''))
        : getParamName(context.prefix, rawParamName)

      const defaultValue = isFilter
        ? (context.defaultState.filters || {})[paramName]
        : (context.defaultState || {})[paramName]
      if (defaultValue !== undefined && defaultValue !== null && defaultValue === paramValue) {
        params.delete(paramName)
      } else if (paramValue) {
        params.set(paramName, encodeURIComponent(JSON.stringify(paramValue)))
      } else if (!paramValue && defaultValue) {
        params.set(paramName, encodeURIComponent(''))
      } else {
        params.delete(paramName)
      }
    },
    [context]
  )
  return changeParam
}

export function useHeadlessTableLink({
  search,
  params: inputParams
}: {
  search: string
  params: { paramName: 'sort' | 'search' | 'limit' | 'skip' | 'quickFilter' | `filters.${string}`; paramValue: any }[]
}) {
  const context = React.useContext(HeadlessTableContext)
  const isClientQuickFilter = context.clientQuickFilters?.includes(context.quickFilter as string)
  const changeParam = useChangeParam()

  const getTo = useCallback(
    (input: Parameters<typeof useHeadlessTableLink>[0]['params']) => {
      if (input.some((p) => p.paramName === 'quickFilter') && isClientQuickFilter) {
        // we have been doing some client-side operations, now we are moving to server-side operations
        // we need to capture to difference between local client state and our current url
        const params = new URLSearchParams()
        input.forEach((p) => changeParam(params, p.paramName, p.paramValue))
        changeParam(params, `search`, context.search)
        context.filterOptions.forEach((f) => changeParam(params, `filters.${f}`, context.filters[f]))
        changeParam(params, `sort`, context.sort)
        return { search }
      } else {
        // all other operations resume from current search
        const params = new URLSearchParams(search)
        input.forEach((p) => changeParam(params, p.paramName, p.paramValue))
        if (
          input.some(
            (p) =>
              p.paramName.startsWith('filters.') ||
              p.paramName === 'search' ||
              p.paramName === 'quickFilter' ||
              p.paramName === 'sort'
          )
        ) {
          changeParam(params, 'skip', 0)
        }
        return { search: params.toString() }
      }
    },
    [search, changeParam, isClientQuickFilter, context]
  )

  const to = React.useMemo(() => getTo(inputParams), [getTo, inputParams])

  const getOnClick = useCallback(
    (input: Parameters<typeof useHeadlessTableLink>[0]['params']) => {
      if (context.filteringTier === 'client' || (isClientQuickFilter && !input.some((p) => p.paramName === 'quickFilter'))) {
        context.setClientParam((p) => {
          const newClientParams = { ...p, filters: { ...p.filters } }
          input.forEach((p) => {
            if (p.paramName.startsWith('filters.')) {
              const filterName = p.paramName.replace('filters.', '')
              newClientParams.filters[filterName] = p.paramValue
            } else {
              newClientParams[p.paramName] = p.paramValue
            }
          })

          if (
            input.some(
              (p) => p.paramName.startsWith('filters.') || p.paramName === 'search' || p.paramName === 'quickFilter'
            )
          ) {
            newClientParams.skip = 0
          }
          return newClientParams
        })
      }
    },
    [context, isClientQuickFilter]
  )

  const onClick = React.useCallback(() => getOnClick(inputParams), [getOnClick, inputParams])

  const hydrated = useHydrated()

  // until hydration happens, no filter is a client filter. For client filters, we return `to` so that filtering will
  // work even before hydration
  const isClientFilter =
    hydrated &&
    (context.filteringTier === 'client' || (isClientQuickFilter && !inputParams.some((p) => p.paramName === 'quickFilter')))

  return {
    to,
    getTo,
    getOnClick,
    onClick,
    isClientFilter
  }
}

export function useHeadlessTableClearAll({ search }: { search: string }) {
  const context = React.useContext(HeadlessTableContext)

  const to = React.useMemo(() => {
    // all other operations resume from current search
    const params = new URLSearchParams(search)

    const toRemove = [...params.keys()].filter((key) => key.startsWith(context.prefix || ''))
    toRemove.forEach((key) => params.delete(key))
    return { search: params.toString() }
  }, [search, context])

  const onClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (context.filteringTier === 'client') {
        event.preventDefault()
        event.stopPropagation()
        context.setClientParam({
          sort: context.defaultState.sort || context.sortOptions[0],
          filters:
            (context.defaultState.filters || context.filterOptions?.reduce((acc, f) => ({ ...acc, [f]: null }), {})) ?? {},
          limit: context.defaultState.limit || 30,
          skip: context.defaultState.skip || 0,
          search: context.defaultState.search || '',
          quickFilter: context.defaultState.quickFilter || null
        })
      }
    },
    [context]
  )
  return { to, onClick }
}

export type HeadlessTableSearchFormProps<Tag extends React.ElementType<any>> = OverrideComponentProps<Tag>

export const HeadlessTableSearchForm = React.forwardRef<HTMLFormElement, HeadlessTableSearchFormProps<'form'>>(
  ({ children, as = 'form', ...props }, forwardedRef) => {
    const context = React.useContext(HeadlessTableContext)
    const isClientQuickFilter = context.clientQuickFilters?.includes(context.quickFilter as string)

    const onSubmit = React.useCallback(
      (event: React.FormEvent<HTMLFormElement>) => {
        if (context.filteringTier === 'client' || isClientQuickFilter) {
          event.preventDefault()
          event.stopPropagation()
          const formData = new FormData(event.currentTarget)
          const name = getParamName(context.prefix, 'search')

          const value = formData.get(name)
          context.setClientParam((p) => ({
            ...p,
            quickFilter: null,
            filters: Object.keys(context.defaultState.filters ?? {})
              .map((name) => ({ [name]: null }))
              .reduce((acc, f) => ({ ...acc, ...f }), {}),
            search: value as string,
            skip: 0,
            sort: 'mostRelevant'
          }))
        }
      },
      [context, isClientQuickFilter]
    )

    return React.createElement(
      as,
      {
        ...props,
        ref: forwardedRef,
        onSubmit: composeEventHandlers(onSubmit, props.onSubmit, { checkForDefaultPrevented: true })
      },
      [
        ...React.Children.toArray(typeof children === 'function' ? children({}) : children),
        <HeadlessTableSearchFormValue key="headless-table-search-form-sort" paramName="sort" paramValue="mostRelevant" />,
        Object.keys(context.defaultState.filters ?? {}).map((f) => (
          <HeadlessTableSearchFormValue
            key={`headless-table-search-form-filter-${f}`}
            paramName={`filters.${f}`}
            paramValue={null}
          />
        )),
        ...(context.defaultState.quickFilter
          ? [
              <HeadlessTableSearchFormValue
                key="headless-table-search-form-quickFilter"
                paramName="quickFilter"
                paramValue={null}
              />
            ]
          : [])
      ]
    )
  }
) as React.FC<HeadlessTableSearchFormProps<React.ElementType<any>>> & {
  <T extends React.ElementType<any> = 'form'>(
    props: HeadlessTableSearchFormProps<T>
  ): ReturnType<React.FC<HeadlessTableSearchFormProps<T>>>
}
HeadlessTableSearchForm.displayName = 'HeadlessTableSearchForm'

function HeadlessTableSearchFormValue({ paramName: rawParamName, paramValue }: { paramName: ParamName; paramValue: any }) {
  const context = React.useContext(HeadlessTableContext)

  const isFilter = rawParamName.startsWith('filters.')
  const paramName = isFilter
    ? getParamName(context.prefix, rawParamName.replace('filters.', ''))
    : getParamName(context.prefix, rawParamName)

  const defaultValue = isFilter ? (context.defaultState.filters || {})[paramName] : (context.defaultState || {})[paramName]
  if (defaultValue !== undefined && defaultValue !== null && defaultValue === paramValue) {
    return null
  } else if (paramValue) {
    return <input name={paramName} value={JSON.stringify(paramValue)} type="hidden" />
  } else if (!paramValue && defaultValue) {
    return <input name={paramName} value={'""'} type="hidden" />
  } else {
    return null
  }
}

export const HeadlessTableSearchInput = React.forwardRef<
  HTMLInputElement,
  Omit<React.ComponentProps<'input'>, 'value' | 'name'>
>((props, ref) => {
  const context = React.useContext(HeadlessTableContext)
  const [search, setSearch] = React.useState(context.search || '')
  React.useEffect(() => {
    setSearch(context.search || '')
  }, [context.search])

  return (
    <input
      {...props}
      ref={ref}
      type="text"
      name={getParamName(context.prefix, 'search')}
      value={search}
      onChange={composeEventHandlers((e) => setSearch(e.currentTarget.value), props.onChange)}
    />
  )
})

HeadlessTableSearchInput.displayName = 'HeadlessTableSearchInput'
